/**
 * Bid package surfaces (Plan G): Bid Form, Proposal Letter, Clarification Letter.
 * Fed from one-pager + clarifications + SOV — editable narrative + exportable text.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useCbApi } from './cbApi';
import {
  SheetShell, HeaderButton, ActionButton, HelpModal,
  LoadingState, ErrorState, NoEstimateState, useToast, fmt,
} from './CbUi';

const storageKey = (estimateId, kind) => `cb_bid_package_${kind}_${estimateId}`;

function useDraft(estimateId, kind, buildDefault) {
  const [text, setText] = useState('');
  useEffect(() => {
    if (!estimateId) return;
    try {
      const saved = localStorage.getItem(storageKey(estimateId, kind));
      if (saved) setText(saved);
      else setText(buildDefault());
    } catch {
      setText(buildDefault());
    }
  }, [estimateId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useCallback((next) => {
    setText(next);
    try { localStorage.setItem(storageKey(estimateId, kind), next); } catch (_) { /* ignore */ }
  }, [estimateId, kind]);

  return [text, save, setText];
}

const shellProps = {
  bid: { title: '📝 Bid Form Submission', accent: '#b45309', guideId: 'cb-bid-form-help', icon: '📝' },
  proposal: { title: '📨 Proposal Letter', accent: '#1d4ed8', guideId: 'cb-proposal-letter-help', icon: '📨' },
  clarification: { title: '💬 Clarification Letter', accent: '#7c3aed', guideId: 'cb-clarification-letter-help', icon: '💬' },
};

const BidPackageSheet = ({ estimate, kind }) => {
  const meta = shellProps[kind];
  const api = useCbApi();
  const { showToast, ToastEl } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bundle, setBundle] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  const load = useCallback(async () => {
    if (!estimate?.id) return;
    setLoading(true);
    try {
      const [one, clar, sov] = await Promise.all([
        api.get(`/cost-breakdown/one-pager/${estimate.id}`),
        api.get(`/cost-breakdown/clarifications?estimate_id=${estimate.id}`).catch(() => ({ data: { data: [] } })),
        api.get(`/cost-breakdown/client-wbs?estimate_id=${estimate.id}`).catch(() => ({ data: { data: [] } })),
      ]);
      setBundle({
        onePager: one.data,
        clarifications: clar.data?.data || [],
        sov: sov.data?.data || [],
      });
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [api, estimate?.id]);

  useEffect(() => { load(); }, [load]);

  const defaultText = useMemo(() => {
    if (!bundle || !estimate) return '';
    const totals = bundle.onePager?.totals || {};
    const costTypes = bundle.onePager?.costTypeTotals || {};
    const lines = Object.entries(costTypes).map(([k, v]) =>
      `  - ${k}: ${fmt(v.dollar_cost, 'currency')} (sell ${fmt(v.selling, 'currency')})`
    );

    if (kind === 'bid') {
      return [
        `BID FORM — ${estimate.estimate_number}`,
        `Project: ${estimate.estimate_name || ''}`,
        `Client: ${estimate.client_name || ''}`,
        '',
        'COST SUMMARY (from CB One-Pager)',
        ...lines,
        '',
        `Grand cost: ${fmt(totals.dollar_cost, 'currency')}`,
        `Grand selling: ${fmt(totals.selling, 'currency')}`,
        `Man hours: ${fmt(totals.man_hours, 'number')}`,
        '',
        'SOV / WBS lines: ' + (bundle.sov.length || 0),
        '',
        'Notes / exclusions:',
        '- ',
      ].join('\n');
    }

    if (kind === 'proposal') {
      return [
        `Dear ${estimate.client_name || 'Sir/Madam'},`,
        '',
        `Re: Proposal for ${estimate.estimate_name || estimate.estimate_number} (${estimate.estimate_number})`,
        '',
        'Laframboise Group is pleased to submit our proposal for the referenced scope.',
        '',
        `Our estimated selling price is ${fmt(totals.selling, 'currency')} based on approximately ${fmt(totals.man_hours, 'number')} man-hours, subject to the clarifications and exclusions attached.`,
        '',
        'We look forward to discussing this opportunity with you.',
        '',
        'Sincerely,',
        'Laframboise Group Ltd. — Estimating',
      ].join('\n');
    }

    // clarification letter
    const clarLines = (bundle.clarifications || []).slice(0, 40).map((c, i) =>
      `${i + 1}. ${c.description || c.clarification || c.notes || JSON.stringify(c)}`
    );
    return [
      `CLARIFICATION LETTER — ${estimate.estimate_number}`,
      `Project: ${estimate.estimate_name || ''}`,
      '',
      'Please find below our clarifications and assumptions forming part of this bid:',
      '',
      ...(clarLines.length ? clarLines : ['1. (Add clarifications on the Clarifications sheet — they will appear here on refresh.)']),
      '',
      'End of clarifications.',
    ].join('\n');
  }, [bundle, estimate, kind]);

  const [text, save] = useDraft(estimate?.id, kind, () => defaultText);

  // When bundle loads and draft empty, fill default
  useEffect(() => {
    if (!loading && bundle && estimate?.id) {
      try {
        const saved = localStorage.getItem(storageKey(estimate.id, kind));
        if (!saved) save(defaultText);
      } catch (_) { /* ignore */ }
    }
  }, [loading, bundle, defaultText, estimate?.id, kind, save]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      showToast('Copied to clipboard');
    } catch {
      showToast('Copy failed', 'error');
    }
  };

  const regenerate = () => {
    save(defaultText);
    showToast('Regenerated from live CB data');
  };

  if (!estimate?.id) return <NoEstimateState section={meta.title} />;
  if (loading) return <LoadingState label={`Building ${meta.title}…`} />;
  if (error) return <ErrorState error={error} onRetry={load} />;

  return (
    <>
      <SheetShell
        title={meta.title}
        subtitle={`Estimate ${estimate.estimate_number} · live from One-Pager / Clarifications / SOV`}
        gradient={`linear-gradient(135deg, ${meta.accent}, ${meta.accent}bb)`}
        headerActions={
          <>
            <HeaderButton onClick={load}>🔄 Refresh data</HeaderButton>
            <HeaderButton onClick={regenerate}>✨ Regenerate draft</HeaderButton>
            <HeaderButton onClick={copy}>📋 Copy</HeaderButton>
            <HeaderButton onClick={() => setShowHelp(true)}>❓ Help</HeaderButton>
          </>
        }
        kpiCards={[
          { label: 'Selling', value: fmt(bundle?.onePager?.totals?.selling, 'currency'), icon: '💵', color: meta.accent },
          { label: 'Cost', value: fmt(bundle?.onePager?.totals?.dollar_cost, 'currency'), icon: '📊', color: '#0f766e' },
          { label: 'Clarifs', value: bundle?.clarifications?.length || 0, icon: '💬', color: '#7c3aed' },
          { label: 'SOV lines', value: bundle?.sov?.length || 0, icon: '📑', color: '#b45309' },
        ]}
        toolbar={
          <div style={{ fontSize: 12.5, color: '#64748b' }}>
            Edit freely — drafts save in this browser. Regenerate pulls fresh numbers from the Cost Breakdown.
          </div>
        }
      >
        <textarea
          value={text}
          onChange={(e) => save(e.target.value)}
          style={{
            width: '100%', minHeight: 420, boxSizing: 'border-box',
            padding: 16, border: '1px solid #e2e8f0', borderRadius: 12,
            fontFamily: 'Georgia, "Times New Roman", serif', fontSize: 14, lineHeight: 1.55,
            background: '#fffef8',
          }}
        />
        <div style={{ marginTop: 12 }}>
          <ActionButton color={meta.accent} onClick={copy}>Copy letter</ActionButton>
        </div>
        {ToastEl}
      </SheetShell>

      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} title={`${meta.title} Help`} accent={meta.accent} guideId={meta.guideId}>
        <p>This draft is built from the live CB One-Pager, Clarifications, and SOV for the active estimate. Edit the text, copy into Word/Outlook, or regenerate when numbers change.</p>
      </HelpModal>
    </>
  );
};

export const BidFormView = (props) => <BidPackageSheet {...props} kind="bid" />;
export const ProposalLetterView = (props) => <BidPackageSheet {...props} kind="proposal" />;
export const ClarificationLetterView = (props) => <BidPackageSheet {...props} kind="clarification" />;

export default BidPackageSheet;
